import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  assumeAdminSupportConversation,
  closeAdminSupportConversation,
  getAdminSupportConversation,
  getAdminSupportConversationSummary,
  listAdminSupportConversations,
  openAdminSupportConversationTicket,
  replyAdminSupportConversation,
  syncAdminSupportKnowledge,
  AdminSupportConversationSummary,
} from "../../../../services/superAdmin/adminSupportConversationService";
import {
  SupportConversation,
  SupportConversationMessage,
  SupportConversationTicketCategory,
} from "../../../../services/supportConversationService";

const CHAT_CATEGORIES: SupportConversationTicketCategory[] = [
  "agenda",
  "orders",
  "payments",
  "account",
  "integration",
  "other",
];

const statusColor = (status: SupportConversation["status"]): string => {
  switch (status) {
    case "queued_human":
      return "danger";
    case "assigned":
      return "warning";
    case "open_ai":
      return "primary";
    default:
      return "secondary";
  }
};

const formatMessageDateTime = (iso: string | null | undefined, locale: string): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  return new Intl.DateTimeFormat(locale, {
    ...(sameDay
      ? { hour: "2-digit", minute: "2-digit" }
      : {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
  }).format(date);
};

const SuperAdminSupportConversations = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState<SupportConversation[]>([]);
  const [active, setActive] = useState<SupportConversation | null>(null);
  const [summary, setSummary] = useState<AdminSupportConversationSummary | null>(null);
  const [statusFilter, setStatusFilter] = useState("queued_human");
  const [draft, setDraft] = useState("");
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketCategory, setTicketCategory] =
    useState<SupportConversationTicketCategory>("other");
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const loadList = useCallback(async () => {
    try {
      const [listRes, summaryRes] = await Promise.all([
        listAdminSupportConversations({
          per_page: 50,
          status: statusFilter || undefined,
        }),
        getAdminSupportConversationSummary(),
      ]);
      if (listRes.success) {
        setItems(listRes.data || []);
      }
      if (summaryRes.success) {
        setSummary(summaryRes.data);
      }
    } catch {
      toast.error(t("adminSupportChat.loadError"));
    }
  }, [statusFilter, t]);

  const loadOne = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const response = await getAdminSupportConversation(id);
        if (response.success && response.data) {
          setActive(response.data);
        }
      } catch {
        toast.error(t("adminSupportChat.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    void (async () => {
      setLoading(true);
      await loadList();
      setLoading(false);
    })();
  }, [loadList]);

  useEffect(() => {
    if (conversationId) {
      void loadOne(Number(conversationId));
    } else {
      setActive(null);
    }
  }, [conversationId, loadOne]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length]);

  const handleAssume = async () => {
    if (!active) return;
    setSending(true);
    try {
      const response = await assumeAdminSupportConversation(active.id);
      if (response.success && response.data) {
        setActive(response.data);
        toast.success(t("adminSupportChat.assumed"));
        await loadList();
      }
    } catch {
      toast.error(t("adminSupportChat.actionError"));
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!active || !draft.trim()) return;
    setSending(true);
    try {
      const response = await replyAdminSupportConversation(active.id, draft.trim());
      if (response.success && response.data) {
        setDraft("");
        setActive(response.data.conversation);
        await loadList();
      }
    } catch {
      toast.error(t("adminSupportChat.actionError"));
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!active) return;
    setSending(true);
    try {
      const response = await closeAdminSupportConversation(active.id);
      if (response.success && response.data) {
        setActive(response.data);
        toast.success(t("adminSupportChat.closed"));
        await loadList();
      }
    } catch {
      toast.error(t("adminSupportChat.actionError"));
    } finally {
      setSending(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await syncAdminSupportKnowledge();
      if (response.success && response.data) {
        toast.success(
          t("adminSupportChat.synced", {
            upserted: response.data.upserted,
            skipped: response.data.skipped,
          })
        );
      }
    } catch {
      toast.error(t("adminSupportChat.syncError"));
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenTicket = () => {
    if (!active || active.status === "closed") {
      return;
    }
    setTicketCategory("other");
    setTicketModalOpen(true);
  };

  const confirmOpenTicket = async () => {
    if (!active || active.status === "closed") {
      return;
    }
    setSending(true);
    try {
      const response = await openAdminSupportConversationTicket(
        active.id,
        ticketCategory
      );
      if (response.success && response.data) {
        setActive(response.data.conversation);
        toast.success(
          t("adminSupportChat.ticketOpened", { id: response.data.ticket.id })
        );
        await loadList();
        setTicketModalOpen(false);
      }
    } catch {
      toast.error(t("adminSupportChat.actionError"));
    } finally {
      setSending(false);
    }
  };

  const messages: SupportConversationMessage[] = active?.messages || [];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("adminSupportChat.title")}
          pageTitle={t("adminSupportChat.breadcrumb")}
        />
        <ToastContainer />
        <Row className="mb-3">
          <Col md={8} className="d-flex flex-wrap gap-2 align-items-center">
            <Badge color="danger" pill>
              {t("adminSupportChat.summary.queued", {
                count: summary?.queued_human ?? 0,
              })}
            </Badge>
            <Badge color="warning" pill>
              {t("adminSupportChat.summary.assigned", {
                count: summary?.assigned ?? 0,
              })}
            </Badge>
            <Badge color="primary" pill>
              {t("adminSupportChat.summary.openAi", {
                count: summary?.open_ai ?? 0,
              })}
            </Badge>
          </Col>
          <Col md={4} className="text-md-end">
            <Button color="soft-secondary" size="sm" onClick={handleSync} disabled={syncing}>
              {syncing ? <Spinner size="sm" /> : t("adminSupportChat.syncKnowledge")}
            </Button>
          </Col>
        </Row>
        <Row>
          <Col xl={4} className="mb-3">
            <Card>
              <CardBody>
                <div className="d-flex gap-2 mb-3">
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="queued_human">
                      {t("adminSupportChat.filter.queued")}
                    </option>
                    <option value="assigned">
                      {t("adminSupportChat.filter.assigned")}
                    </option>
                    <option value="open_ai">{t("adminSupportChat.filter.openAi")}</option>
                    <option value="">{t("adminSupportChat.filter.all")}</option>
                  </Input>
                </div>
                {loading && !active ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-muted">{t("adminSupportChat.empty")}</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`btn btn-soft-${statusColor(item.status)} text-start`}
                        onClick={() =>
                          navigate(`/super-admin/support/conversations/${item.id}`)
                        }
                      >
                        <div className="d-flex justify-content-between">
                          <strong>#{item.id}</strong>
                          <Badge color={statusColor(item.status)}>
                            {t(`supportChat.status.${item.status}`)}
                          </Badge>
                        </div>
                        <small className="d-block">
                          {item.tenant?.name || `Tenant #${item.tenant_id}`}
                        </small>
                        <small className="text-muted d-block">
                          {item.user?.name || item.user?.email || "—"}
                        </small>
                        {item.latest_user_message?.body ? (
                          <small
                            className="d-block text-body-secondary mt-1 text-truncate"
                            title={item.latest_user_message.body}
                          >
                            {item.latest_user_message.body}
                          </small>
                        ) : (
                          <small className="d-block text-muted mt-1">
                            {t("adminSupportChat.noUserMessage")}
                          </small>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xl={8}>
            <Card>
              <CardBody>
                {!active ? (
                  <div className="text-muted text-center py-5">
                    {t("adminSupportChat.select")}
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
                      <div>
                        <h5 className="mb-1">
                          {t("adminSupportChat.conversation")} #{active.id}
                        </h5>
                        <div className="text-muted small">
                          {active.tenant?.name} · {active.user?.email}
                        </div>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {active.status !== "closed" && active.status !== "assigned" ? (
                          <Button
                            color="warning"
                            size="sm"
                            onClick={handleAssume}
                            disabled={sending}
                          >
                            {t("adminSupportChat.assume")}
                          </Button>
                        ) : null}
                        {active.status !== "closed" && !active.support_ticket_id ? (
                          <Button
                            color="info"
                            outline
                            size="sm"
                            onClick={handleOpenTicket}
                            disabled={sending}
                          >
                            {t("adminSupportChat.openTicket")}
                          </Button>
                        ) : null}
                        {active.status !== "closed" ? (
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            onClick={handleClose}
                            disabled={sending}
                          >
                            {t("adminSupportChat.close")}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {active.support_ticket_id ? (
                      <div className="alert alert-info py-2">
                        {t("adminSupportChat.ticketLinked", {
                          id: active.support_ticket_id,
                        })}{" "}
                        <Link to={`/super-admin/support/tickets/${active.support_ticket_id}`}>
                          #{active.support_ticket_id}
                        </Link>
                      </div>
                    ) : null}

                    <div
                      className="border rounded p-3 mb-3 bg-light"
                      style={{ minHeight: 280, maxHeight: 420, overflowY: "auto" }}
                    >
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`mb-3 ${
                            message.author_type === "agent" ? "text-end" : "text-start"
                          }`}
                        >
                          <div
                            className={`d-inline-block text-start p-2 rounded bg-white border`}
                            style={{ maxWidth: "85%" }}
                          >
                            <div className="small fw-semibold mb-1">
                              {t(`supportChat.author.${message.author_type}`)}
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{message.body}</div>
                            {message.created_at ? (
                              <div className="small text-muted mt-1">
                                {formatMessageDateTime(message.created_at, i18n.language)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      <div ref={bottomRef} />
                    </div>

                    {active.status !== "closed" ? (
                      <div className="d-flex gap-2">
                        <Input
                          type="textarea"
                          rows={2}
                          value={draft}
                          disabled={sending}
                          placeholder={t("adminSupportChat.placeholder")}
                          onChange={(e) => setDraft(e.target.value)}
                        />
                        <Button
                          color="primary"
                          onClick={handleReply}
                          disabled={sending || !draft.trim()}
                        >
                          {sending ? <Spinner size="sm" /> : t("adminSupportChat.send")}
                        </Button>
                      </div>
                    ) : (
                      <div className="alert alert-secondary mb-0">
                        {t("adminSupportChat.closedHint")}
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal
          isOpen={ticketModalOpen}
          toggle={() => !sending && setTicketModalOpen(false)}
          centered
        >
          <ModalHeader toggle={() => !sending && setTicketModalOpen(false)}>
            {t("supportChat.categoryModalTitle")}
          </ModalHeader>
          <ModalBody>
            <Label for="admin-support-chat-category">
              {t("supportChat.categoryLabel")}
            </Label>
            <Input
              id="admin-support-chat-category"
              type="select"
              value={ticketCategory}
              onChange={(e) =>
                setTicketCategory(
                  e.target.value as SupportConversationTicketCategory
                )
              }
            >
              {CHAT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(`support.categories.${category}`)}
                </option>
              ))}
            </Input>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={sending}
              onClick={() => setTicketModalOpen(false)}
            >
              {t("supportChat.categoryCancel")}
            </Button>
            <Button
              color="primary"
              disabled={sending}
              onClick={() => void confirmOpenTicket()}
            >
              {sending ? <Spinner size="sm" /> : t("adminSupportChat.openTicket")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SuperAdminSupportConversations;
